<template>
  <ValidationObserver
    ref="form"
    v-slot="{ handleSubmit }"
    slim
  >
    <form
      role="form"
      class="user form-horizontal"
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <fieldset>
        <!-- Name Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_NAME')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_NAME_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionName"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-4">
                  <input
                    v-model="authConnection.name"
                    type="text"
                    name="authConnectionName"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Name Field End -->

        <!-- URL Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_URL')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_URL_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes, errors }"
              ref="authConnectionUrl"
              rules="required|url"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-7">
                  <HttpUrl
                    v-model="authConnection.url"
                    :name="'authConnectionUrl'"
                  />             
                </div>
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-2 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- URL Field End -->

        <!--Headers Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_HEADERS')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_HEADERS_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionHeaders"
              slim
            >
              <div :class="classes">
                <div class="col-sm-8 col-md-6">
                  <HttpHeaders
                    v-model="authConnection.headers"
                    name="authConnectionHeaders"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Headers Field End -->

        <!-- HTTP Method Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_METHOD')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_METHOD_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionMethod"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-4">
                  <HttpMethodSelector
                    v-model="authConnection.method"
                    name="authConnectionMethod"
                  />
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- HTTP Method Field End -->

        <!-- Content Type Field Start -->
        <Field
          v-if="showBody"
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_CONTENT_TYPE')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_CONTENT_TYPE_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionContentType"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-4">
                  <HttpContentTypeSelector
                    v-model="authConnection.contentType"
                    name="authConnectionContentType"
                  />
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Content Type Field End -->

        <!-- Body Field Start -->
        <Field
          v-if="showBody"
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_BODY')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_BODY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionBody"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-4">
                  <HttpBody
                    v-model="authConnection.body"
                    :body-type="authConnection.contentType"
                    name="authConnectionBody"
                  />
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Body Field End -->

        <!-- Timeout Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_TIMEOUT')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_TIMEOUT_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionTimeout"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-2">
                  <input
                    v-model="authConnection.pingTimeoutInMS"
                    type="number"
                    name="authConnectionTimeout"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Timeout Field End -->
      </fieldset>

      <!-- Static Token values Section Start -->
      <fieldset>
        <legend class="col-lg-offset-1 col-lg-10">
          <span v-t="'TOKEN_USAGE_SECTION'" />
          <a
            v-tooltip.right="$t('TOKEN_USAGE_SECTION_TT')"
            class="m-l-10 f-s-16"
          >
            <i class="fa fa-question-circle" />
          </a>
        </legend>

        <!-- Auth Header Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_AUTH_HEADER')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_AUTH_HEADER_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionHeaderName"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-4">
                  <input
                    v-model="authConnection.authorizationHeaderName"
                    placeholder="Authorization"
                    type="text"
                    name="authConnectionHeaderName"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Auth Header Field End -->

        <!-- Default Token Type Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_TOKEN_TYPE')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_TOKEN_TYPE_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionTokenType"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-4">
                  <input
                    v-model="authConnection.defaultTokenType"
                    type="text"
                    placeholder="Bearer"
                    name="authConnectionTokenType"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field> 
        <!-- Default Token Type Field End -->

        <!-- Default Token Expiration Field Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_DEFAULT_EXPIRATION')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_DEFAULT_EXPIRATION_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="defaultTokenExpireInSeconds"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-2">
                  <input
                    v-model="authConnection.defaultTokenExpireInSeconds "
                    type="number"
                    placeholder="3600"
                    name="defaultTokenExpireInSeconds"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field> 
        <!-- Default Token Expiration Field End -->
      </fieldset>

      <!-- Request Response Parsing Section Start -->
      <fieldset>
        <legend class="col-lg-offset-1 col-lg-10">
          <span v-t="'REQUEST_PARSING_SECTION'" />
          <a
            v-tooltip.right="$t('REQUEST_PARSING_SECTION_TT')"
            class="m-l-10 f-s-16"
          >
            <i class="fa fa-question-circle" />
          </a>
        </legend>

        <!-- Dynamic Token Parsing Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_DYNAMIC_TOKEN')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_DYNAMIC_TOKEN_TT')"
        >
          <template #element>
            <div class="col-sm-8 col-md-8">
              <HttpParseSettings
                v-model="authConnection.parseSettings.dynamicAccessToken"
                name="authConnectionDynamicToken"
              />
            </div>                   
          </template>
        </Field>
        <!-- Dynamic Token Parsing End -->

        <!-- Dynamic Token Type Parsing Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_DYNAMIC_TYPE')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_DYNAMIC_TYPE_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionDynamicTokenType"
              slim
            >
              <div :class="classes">
                <div class="col-sm-8 col-md-8">
                  <HttpParseSettings
                    v-model="authConnection.parseSettings.dynamicTokenType"
                    name="authConnectionDynamicTokenType"
                  />
                </div>                   
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <!-- Dynamic Token Type Parsing End -->

        <!-- Dynamic Token Expiration Parsing Start -->
        <Field
          :label="$t('INPUT_LABEL_AUTH_CONNECTION_DYNAMIC_EXPIRATION')"
          :has-tooltip="true"
          :tooltip-text="$t('INPUT_LABEL_AUTH_CONNECTION_DYNAMIC_EXPIRATION_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="authConnectionDynamicExpiration"
              slim
            >
              <div :class="classes">
                <div class="col-sm-8 col-md-8">
                  <HttpParseSettings
                    v-model="authConnection.parseSettings.dynamicExpireIn"
                    name="authConnectionDynamicExpiration"
                  />
                </div>                   
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- Dynamic Token Expiration Parsing Start -->

        <div class="form-group">
          <div class="col-sm-8 col-md-8 col-md-offset-3 col-sm-offset-3">            
            <PrimaryButton
              size="sm"
              :show-loader-on-form="showLoader"
            >
              <span v-t="currentAuthConnection.id ? 'UPDATE' : 'CREATE'" />
            </PrimaryButton>

            <button
              v-t="'CANCEL'"
              type="button"
              class="btn btn-sm btn-danger m-l-15"
              @click="cancel"
            />
          </div>
        </div>
      </fieldset>
    </form>
  </ValidationObserver>
</template>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import PrimaryButton from '@/core/components/PrimaryButton';
import HttpUrl from '@/core/components/HttpForm/HttpUrl';
import HttpContentTypeSelector from '@/core/components/HttpForm/HttpContentTypeSelector';
import HttpMethodSelector from '@/core/components/HttpForm/HttpMethodSelector';
import HttpBody from '@/core/components/HttpForm/HttpBody';
import HttpHeaders from '@/core/components/HttpForm/HttpHeaders';
import HttpParseSettings from '@/core/components/HttpForm/HttpParseSettings';

export default {
  name: 'AuthConnectionForm',
  components: { Field, PrimaryButton, HttpUrl, HttpContentTypeSelector, HttpMethodSelector, HttpBody, HttpHeaders, HttpParseSettings },
  props: {
    showLoader: {
      type: Boolean,
      default: () => true
    }, 
    currentAuthConnection: {
      type: Object,
      default: function() {
        return {
          method: 'get',
          contentType: 'application/json',
          headers: {},
          parseSettings: {
            dynamicAccessToken: [],
            dynamicTokenType: [],
            dynamicExpireIn: [],
          }
        };
      }
    }
  },
  data() {
    return {
      authConnection:  _.cloneDeep(this.currentAuthConnection)
    };
  },
  computed: {
    showBody() {
      return this.authConnection.method == 'post' || this.authConnection.method == 'patch' || this.authConnection.method == 'put';
    },
  },
  watch: {
    currentAuthConnection(value) {
      this.authConnection = _.cloneDeep(value);
    }
  },
  methods:{
    onSubmit() {
      if (!this.authConnection.id) {
        this.authConnection.dynamicAccessToken = this.authConnection.parseSettings.dynamicAccessToken;
        this.authConnection.dynamicTokenType = this.authConnection.parseSettings.dynamicTokenType;
        this.authConnection.dynamicExpireIn = this.authConnection.parseSettings.dynamicExpireIn;
      }
     
      this.$emit('submit', this.authConnection);
    },
    cancel() {
      this.$emit('cancel', this.authConnection);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>